import React from 'react';

import { ChargesManagerProgramControls } from './charges-manager/controls';
import { CovidProgramControls } from './covid/controls';
import { CovidTestingProgramControls } from './covid-testing/controls';
import { CovidVaccineProgramControls } from './covid-vaccine/controls';
import { DshProgramControls } from './dsh/controls';
import { FistProgramControls } from './fist/controls';
import { InfluenzaProgramControls } from './influenza-vaccine/controls';
import { InrepProgramControls } from './inrep/controls';
import { LaiCommunityProgramControls } from './lai-community/controls';
import { LaiProgramControls } from './lai/controls';
import { LeadProgramControls } from './lead/controls';
import { MaternalProgramControls } from './maternal/controls';
import { MistProgramControls } from './mist/controls';
import { OdrProgramControls } from './odr/controls';
import { OliveProgramControls } from './olive/controls';
import { ProgressReportProgramControls } from './progress-report/controls';
import type { CandidateProgramFilterValues } from '../utils/candidateProgramFilters';

type ProgramFilterChange = <K extends keyof CandidateProgramFilterValues>(
  key: K,
  value: CandidateProgramFilterValues[K],
) => void;

type ProgressOption = { label: string; value: string };
type LeadReferralExportSite = { label: string; site: string | null };

export type RenderProgramControlsArgs = {
  programKey: string;
  values: CandidateProgramFilterValues;
  onFilterChange: ProgramFilterChange;
  actionRowClass: string;
  renderStandardActionButtons: () => React.ReactNode;
  renderReportActionLabel: (label: string) => React.ReactNode;
  onOpenCandidatesAction: (action: string) => void;
  onSubmitReport: (fieldsMap: Record<string, string>) => void;
  mistReportLoading: null | 'active' | 'released' | 'all';
  onDownloadMistReport: (reportType: 'active' | 'released' | 'all') => void;
  covidTestingProgramOptions: string[];
  covidVaccineProgramOptions: string[];
  influenzaProgramOptions: string[];
  influenzaNotReceivedOptions: string[];
  inrepReportProgramOptions: string[];
  onOpenInrepReportDialog: () => void;
  progressReportHubOptions: ProgressOption[];
  progressReportAppearanceOptions: ProgressOption[];
  progressReportProviderSuggestions: string[];
  leadReferralExportSites: LeadReferralExportSite[];
};

type ProgramControlsRenderer = (args: RenderProgramControlsArgs) => React.ReactNode;

const toCommonProps = (args: RenderProgramControlsArgs) => ({
  values: args.values,
  onFilterChange: args.onFilterChange,
  actionRowClass: args.actionRowClass,
  renderStandardActionButtons: args.renderStandardActionButtons,
  renderReportActionLabel: args.renderReportActionLabel,
});

const renderOliveControls: ProgramControlsRenderer = (args) => (
  <OliveProgramControls
    {...toCommonProps(args)}
    isOliveProgram={args.programKey === 'olive'}
    isOliveVistaProgram={args.programKey === 'olive-vista'}
    onOpenCandidatesAction={args.onOpenCandidatesAction}
    onSubmitReport={args.onSubmitReport}
  />
);

const PROGRAM_CONTROLS_RENDERERS: Record<string, ProgramControlsRenderer> = {
  olive: renderOliveControls,
  'olive-vista': renderOliveControls,
  mist: (args) => (
    <MistProgramControls
      {...toCommonProps(args)}
      mistReportLoading={args.mistReportLoading}
      onDownloadMistReport={args.onDownloadMistReport}
    />
  ),
  fist: (args) => (
    <FistProgramControls
      {...toCommonProps(args)}
      onSubmitReport={args.onSubmitReport}
    />
  ),
  dsh: (args) => (
    <DshProgramControls
      {...toCommonProps(args)}
      onOpenCandidatesAction={args.onOpenCandidatesAction}
      onSubmitReport={args.onSubmitReport}
    />
  ),
  odr: (args) => (
    <OdrProgramControls
      {...toCommonProps(args)}
      onOpenCandidatesAction={args.onOpenCandidatesAction}
      onSubmitReport={args.onSubmitReport}
    />
  ),
  covid: (args) => (
    <CovidProgramControls
      {...toCommonProps(args)}
      onSubmitReport={args.onSubmitReport}
    />
  ),
  'covid-testing': (args) => (
    <CovidTestingProgramControls
      {...toCommonProps(args)}
      covidTestingProgramOptions={args.covidTestingProgramOptions}
      onSubmitReport={args.onSubmitReport}
    />
  ),
  'covid-vaccine': (args) => (
    <CovidVaccineProgramControls
      {...toCommonProps(args)}
      covidVaccineProgramOptions={args.covidVaccineProgramOptions}
      onSubmitReport={args.onSubmitReport}
    />
  ),
  'monkeypox-vaccine': (args) => (
    <CovidVaccineProgramControls
      {...toCommonProps(args)}
      covidVaccineProgramOptions={args.covidVaccineProgramOptions}
      onSubmitReport={args.onSubmitReport}
    />
  ),
  'influenza-vaccine': (args) => (
    <InfluenzaProgramControls
      {...toCommonProps(args)}
      influenzaProgramOptions={args.influenzaProgramOptions}
      influenzaNotReceivedOptions={args.influenzaNotReceivedOptions}
      onSubmitReport={args.onSubmitReport}
    />
  ),
  maternal: (args) => (
    <MaternalProgramControls
      {...toCommonProps(args)}
      onOpenCandidatesAction={args.onOpenCandidatesAction}
      onSubmitReport={args.onSubmitReport}
    />
  ),
  inrep: (args) => (
    <InrepProgramControls
      {...toCommonProps(args)}
      inrepReportProgramOptions={args.inrepReportProgramOptions}
      onOpenInrepReportDialog={args.onOpenInrepReportDialog}
    />
  ),
  'progress-report': (args) => (
    <ProgressReportProgramControls
      {...toCommonProps(args)}
      progressReportHubOptions={args.progressReportHubOptions}
      progressReportAppearanceOptions={args.progressReportAppearanceOptions}
      progressReportProviderSuggestions={args.progressReportProviderSuggestions}
      onSubmitReport={args.onSubmitReport}
    />
  ),
  lead: (args) => (
    <LeadProgramControls
      {...toCommonProps(args)}
      leadReferralExportSites={args.leadReferralExportSites}
      onSubmitReport={args.onSubmitReport}
    />
  ),
  'charges-manager': (args) => (
    <ChargesManagerProgramControls
      {...toCommonProps(args)}
      onSubmitReport={args.onSubmitReport}
    />
  ),
  lai: (args) => (
    <LaiProgramControls
      {...toCommonProps(args)}
      onOpenCandidatesAction={args.onOpenCandidatesAction}
    />
  ),
  'lai-community': (args) => (
    <LaiCommunityProgramControls
      {...toCommonProps(args)}
      onOpenCandidatesAction={args.onOpenCandidatesAction}
    />
  ),
};

export const renderProgramControls = (args: RenderProgramControlsArgs): React.ReactNode => {
  const renderer = PROGRAM_CONTROLS_RENDERERS[args.programKey];
  if (!renderer) {
    return null;
  }

  return renderer(args);
};
